import {Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch} from '@stencil/core';
import {ButtonSizes, DrawerSizes} from '@/types';
import {preventBodyScroll} from '@/utils';

export interface CancelDrawerEventDetails {
    source?: 'disconnectedCallback';
}

/**
 * @slot drawer-header - The drawer title
 * @slot drawer-subheader - The drawer subtitle
 * @slot drawer-body - The drawer main content
 * @slot drawer-cancel - If you need to customize the cancel CTA (with any attribute for a specific need), use this slot
 * @slot drawer-confirm - If you need to customize the confirm CTA (with any attribute for a specific need), use this slot
 */
@Component({
    tag: 'joy-drawer',
    styleUrls: ['./style/drawer.scss'],
    shadow: true,
})
export class Drawer {
    private fireCancelEvent = true;
    private cancelSource?: CancelDrawerEventDetails['source'];

    private get cancelCTASlot(): HTMLElement[] {
        return Array.from(this.host.querySelectorAll('[slot="drawer-cancel"]'));
    }

    private get confirmCTASlot(): HTMLElement[] {
        return Array.from(this.host.querySelectorAll('[slot="drawer-confirm"]'));
    }

    @State() hasFooterSlot = false;
    @Element() host!: HTMLJoyDrawerElement;

    /** Drawer open state */
    @Prop({reflect: true, mutable: true}) open = false;
    /** Drawer sizes */
    @Prop({reflect: true}) size: DrawerSizes = 'default';
    /** Main CTA text. If none given, it won't show the CTA */
    @Prop({reflect: true}) confirmText?: string;
    /** Secondary CTA text. If none given, it won't show the CTA */
    @Prop({reflect: true}) cancelText?: string;
    /** If you want to change the CTA sizes, please choose between small, medium, large */
    @Prop({reflect: true}) buttonSize: ButtonSizes = 'small';
    /**
     * @experimental
     * Removes the overlay and allows interacting with background content
     * */
    @Prop({reflect: true}) elevated = false;

    /** Custom event fired when clicking on confirm button */
    @Event({eventName: 'joy-confirm-drawer'}) joyConfirmDrawer!: EventEmitter<void>;
    /** Custom event fired when clicking on cancel button (even when slotted) or top right cross icon */
    @Event({eventName: 'joy-cancel-drawer'}) joyCancelDrawer!: EventEmitter<CancelDrawerEventDetails>;

    @Listen('keydown', {target: 'document'})
    closeOnEscapePress(ev: any) {
        if (ev.code === 'Escape' && this.open) {
            this.hideDrawer();
        }
    }

    @Watch('open')
    async closeDrawerIfNeeded(newValue: boolean, oldValue: boolean) {
        // When we click on dismiss CTAs, we use the same onClose method.
        // In order not to trigger the same method 2 times, we close it only if it was previously opened (thanks to the oldValue param).
        if (oldValue && !newValue) {
            this.resetPageStateAndListeners();
            if (this.fireCancelEvent) {
                this.joyCancelDrawer.emit({
                    source: this.cancelSource,
                });
            }
        } else if (newValue) {
            await this.openDrawer();
        }
        this.fireCancelEvent = true;
    }

    /** Setting open to false will trigger the upper Watch, reset everything, then trigger the custom cancel event. */
    private hideDrawer = () => {
        this.open = false;
    };

    private resetPageStateAndListeners = () => {
        preventBodyScroll(false);
        this.focusOnTriggerWhenClose();

        if (this.cancelCTASlot) {
            this.cancelCTASlot.forEach((cancel) => cancel.removeEventListener('click', this.closeDrawerListener));
        }
    };

    /**
     * If you want to trigger specific action after the drawer opening.
     * @param {Function} callback
     */
    @Method()
    async openDrawer(callback?: () => any) {
        !this.elevated && preventBodyScroll(true);
        this.open = true;
        this.host.focus();

        if (this.cancelCTASlot) {
            this.cancelCTASlot.forEach((cancel) => cancel.addEventListener('click', this.closeDrawerListener));
        }

        if (callback) {
            callback();
        }
    }

    private closeDrawerListener = async () => {
        return await this.closeDrawer();
    };

    /**
     * Hide the drawer from outside
     * @return {Promise}
     */
    @Method()
    async closeDrawer({fireCancelEvent} = {fireCancelEvent: true}): Promise<void> {
        this.fireCancelEvent = fireCancelEvent;
        return this.hideDrawer();
    }

    private focusOnTriggerWhenClose() {
        const trigger = document.querySelector(`joy-drawer-trigger[drawer="${this.host.id}"] button`);

        // Of course, it won't focus on anything if the drawer is open by default without any trigger
        if (trigger) {
            (trigger as HTMLElement).focus();
        }
    }

    private drawerHasButtons() {
        const buttonSlots = [...this.cancelCTASlot, ...this.confirmCTASlot];
        const hasButtonProps = !!this.cancelText || !!this.confirmText;

        return buttonSlots.length > 0 || hasButtonProps;
    }

    private fireConfirmEvent() {
        this.joyConfirmDrawer.emit();
    }

    connectedCallback() {
        this.closeDrawer = this.closeDrawer.bind(this);
        this.fireConfirmEvent = this.fireConfirmEvent.bind(this);
    }

    async componentWillLoad() {
        if (this.open) {
            await this.openDrawer();
        }

        this.confirmCTASlot.forEach((confirmCta) => {
            confirmCta.addEventListener('click', this.fireConfirmEvent);
        });
    }

    disconnectedCallback() {
        this.cancelSource = 'disconnectedCallback';

        this.confirmCTASlot.forEach((confirmCta) => {
            confirmCta.removeEventListener('click', this.fireConfirmEvent);
        });

        this.hideDrawer();
    }

    render() {
        return (
            <Host role="dialog" aria-modal="true">
                {!this.elevated && (
                    <div
                        class={{
                            'joy-drawer__overlay': true,
                            'joy-drawer__overlay--open': this.open,
                        }}
                        onClick={this.hideDrawer}
                    ></div>
                )}
                <div
                    class={{
                        'joy-drawer--wrapper': true,
                        'joy-drawer--wrapper-open': this.open,
                    }}
                >
                    <div class="joy-drawer">
                        <div class="joy-drawer--header">
                            <div class="joy-drawer--header__title">
                                <slot name="drawer-header" />
                            </div>
                            <p class="joy-drawer--subheader" id="drawerDescription">
                                <slot name="drawer-subheader" />
                            </p>

                            <joy-icon id="joy-drawer--close" tabindex="1" name="cross" class="joy-drawer--close" onClick={this.hideDrawer} />
                        </div>
                        <div class="joy-drawer--body">
                            <slot name="drawer-body" />
                        </div>
                        <div
                            class={{
                                'joy-drawer--footer': true,
                                'joy-drawer--footer-filled': this.drawerHasButtons(),
                            }}
                        >
                            <slot name="drawer-cancel">
                                {this.cancelText && (
                                    <joy-button onClick={this.hideDrawer} class="joy-drawer--cancel" variant="ghost" size={this.buttonSize}>
                                        {this.cancelText}
                                    </joy-button>
                                )}
                            </slot>

                            <slot name="drawer-confirm">
                                {this.confirmText && (
                                    <joy-button onClick={this.fireConfirmEvent} class="joy-drawer--confirm" variant="main" size={this.buttonSize}>
                                        {this.confirmText}
                                    </joy-button>
                                )}
                            </slot>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
